import { useCallback, useMemo, useRef } from 'react'

import { INVALID_FILE_CONTENT_ERROR } from '@/components/ui/file-upload/file-upload.constants'
import {
  getAcceptString,
  ValidatedFile,
  validateBatch,
} from '@/components/ui/file-upload/file-upload.utils'

export type { ValidatedFile }

export interface UseFilePickerOptions {
  files?: File[]
  acceptedTypes?: string[]
  maxFiles?: number
  maxFileSize?: number
  maxOutputFileSize?: number
  multiple?: boolean
  disabled?: boolean
  replaceMode?: boolean
  onChange?: (files: File[]) => void
  onFilesSelected?: (files: File[]) => void
  transformFile?: (file: File) => Promise<File>
  verifyFile?: (file: File) => Promise<boolean>
  onValidationError?: (errors: ValidatedFile[]) => void
}

export interface UseFilePickerReturn {
  inputProps: {
    accept: string
    multiple: boolean
    disabled: boolean
    ref: React.RefObject<HTMLInputElement | null>
    type: 'file'
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  }
  open: () => void
  processFiles: (fileList: FileList) => void
  processFile: (file: File) => void
  atCapacity: boolean
  remaining: number
}

export const useFilePicker = ({
  files = [],
  acceptedTypes = [],
  maxFiles = 1,
  maxFileSize = 0,
  maxOutputFileSize,
  multiple = false,
  disabled = false,
  replaceMode = false,
  onChange,
  onFilesSelected,
  transformFile,
  verifyFile,
  onValidationError,
}: UseFilePickerOptions = {}): UseFilePickerReturn => {
  const effectiveMaxFiles = replaceMode ? 1 : multiple ? maxFiles : 1
  const remaining = effectiveMaxFiles - files.length
  // In replaceMode, replacement is always allowed regardless of capacity
  const atCapacity = !replaceMode && remaining <= 0

  const existingFileNames = useMemo(() => new Set(files.map((f) => f.name)), [files])
  const inputRef = useRef<HTMLInputElement>(null)

  const applyValidFiles = useCallback(
    (valid: File[]) => {
      if (valid.length === 0) return
      onFilesSelected?.(valid)
      onChange?.(replaceMode || !multiple ? [valid[0]] : [...files, ...valid])
    },
    [replaceMode, multiple, files, onChange, onFilesSelected],
  )

  const validateAndApply = useCallback(
    (filesArray: File[], sizeLimit: number) => {
      const { valid, invalid } = validateBatch(filesArray, {
        acceptedTypes,
        existingNames: replaceMode ? new Set() : existingFileNames,
        maxFileSize: sizeLimit,
        maxFiles: effectiveMaxFiles,
        remainingSlots: replaceMode ? 1 : remaining,
      })

      // Valid files first, so a consumer that clears its error on change keeps this batch's errors
      applyValidFiles(valid)

      if (invalid.length > 0) {
        onValidationError?.(invalid)
      }
    },
    [
      acceptedTypes,
      replaceMode,
      existingFileNames,
      effectiveMaxFiles,
      remaining,
      applyValidFiles,
      onValidationError,
    ],
  )

  // `maxFileSize` caps what the user may pick; `maxOutputFileSize` caps what is kept after `transformFile`
  const transformAndValidate = useCallback(
    async (rawFilesArray: File[]) => {
      let filesArray = rawFilesArray

      if (verifyFile) {
        const results = await Promise.all(rawFilesArray.map(verifyFile))
        const rejected = rawFilesArray.filter((_, index) => !results[index])

        if (rejected.length > 0) {
          onValidationError?.(
            rejected.map((file) => ({ errors: [INVALID_FILE_CONTENT_ERROR], file })),
          )
        }

        filesArray = rawFilesArray.filter((_, index) => results[index])

        if (filesArray.length === 0) return
      }

      if (!transformFile) {
        validateAndApply(filesArray, maxFileSize)
        return
      }

      const isTooBig = (file: File) => maxFileSize > 0 && file.size > maxFileSize
      const tooBig = filesArray.filter(isTooBig)

      if (tooBig.length > 0) {
        const { invalid } = validateBatch(tooBig, {
          acceptedTypes,
          maxFileSize,
          maxFiles: tooBig.length,
          remainingSlots: tooBig.length,
        })

        onValidationError?.(invalid)
      }

      const transformed = await Promise.all(
        filesArray.filter((file) => !isTooBig(file)).map(transformFile),
      )

      if (transformed.length > 0) {
        validateAndApply(transformed, maxOutputFileSize ?? maxFileSize)
      }
    },
    [
      verifyFile,
      transformFile,
      maxFileSize,
      maxOutputFileSize,
      acceptedTypes,
      onValidationError,
      validateAndApply,
    ],
  )

  const processFiles = useCallback(
    (rawFiles: FileList) => {
      if (disabled) return
      if (!replaceMode && atCapacity && multiple) return

      transformAndValidate(!replaceMode && multiple ? Array.from(rawFiles) : [rawFiles[0]])
    },
    [disabled, replaceMode, atCapacity, multiple, transformAndValidate],
  )

  const processFile = useCallback(
    (file: File) => {
      if (disabled) return
      if (!replaceMode && atCapacity && multiple) return

      transformAndValidate([file])
    },
    [disabled, replaceMode, atCapacity, multiple, transformAndValidate],
  )

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { files } = e.target
      if (!files?.length) return
      processFiles(files)
      e.target.value = ''
    },
    [processFiles],
  )

  const open = useCallback(() => {
    inputRef.current?.click()
  }, [])

  return {
    atCapacity,
    inputProps: {
      accept: getAcceptString(acceptedTypes),
      disabled,
      multiple: !replaceMode && multiple,
      onChange: handleInputChange,
      ref: inputRef,
      type: 'file',
    },
    open,
    processFile,
    processFiles,
    remaining,
  }
}
